export default `# Decisions

The decisions panel captures commitments and choices made during your session. It uses AI to detect when the conversation reaches a decision point, then presents it for your review.

---

## How Decisions Are Detected

Click **Scan Now** to analyse the transcript for decision language. The AI looks for statements like:
- "We've decided to..."
- "Let's go with..."
- "The agreed approach is..."

Each detected decision includes a **confidence level**:
- **High** (3 bars) — Clear, explicit decision language
- **Medium** (2 bars) — Inferred from context

---

## Decision Table

The table shows all detected decisions with sortable columns:

| Column | Description |
|--------|-------------|
| Status | Pending, Accepted, or Superseded |
| Decision | The decision statement |
| Topic | Category or subject area |
| Confidence | Detection confidence (High/Medium) |
| Speaker | Who made or proposed the decision |
| Time | When it was detected |

Click any row to expand it and see:
- **Transcript snippet** — The original quote that triggered detection
- **Reason** — Why the AI flagged this as a decision
- **Supersedes** — If this replaces an earlier decision
- **Timestamps** — Detection and acceptance details

---

## Reviewing Decisions

For each **Pending** decision (hover to reveal actions):

| Action | Effect |
|--------|--------|
| ✓ Accept | Confirm and record the decision |
| ✏️ Edit & Accept | Modify the wording, then accept |
| ✗ Reject | Dismiss as a false positive |

**Accepted** decisions are locked and marked as "Current".

**Superseded** decisions are grayed out — they were replaced by a newer decision on the same topic.

---

## Filtering & Search

- **Search** — Find decisions by statement, topic, speaker, or reason
- **Status filter** — Show only Pending, Accepted, or Superseded
- **Topic filter** — Narrow to a specific subject area
- **Confidence filter** — Show only High or Medium confidence

---

## Tips

- **Scan regularly** — Run a scan after each major discussion segment to capture decisions while context is fresh
- **Edit before accepting** — The AI's wording may not be perfect; refine it to match what was actually agreed
- **Watch for superseded decisions** — If the group changes direction, the newer decision automatically links to the one it replaces
- **Use topic filters after the session** — Great for reviewing all decisions in a specific area`;
